import axios from 'axios';
import { Calendar, DefaultDateRange } from '@/common/model/calendar';
import { CalendarEvent } from '@/common/model/events';
import { CalendarEditor } from '@/common/model/calendar_editor';
import { CalendarInfo } from '@/common/model/calendar_info';
import { WidgetConfig } from '@/common/model/widget_config';
import ModelService from '@/client/service/models';
import { UrlNameAlreadyExistsError, InvalidUrlNameError, CalendarNotFoundError } from '@/common/exceptions/calendar';
import { CalendarEditorPermissionError, EditorAlreadyExistsError, EditorNotFoundError } from '@/common/exceptions/editor';
import { UnauthenticatedError, UnknownError, EmptyValueError, AccountInviteAlreadyExistsError } from '@/common/exceptions';
import { useCalendarStore } from '@/client/stores/calendarStore';
import { validateAndEncodeId, handleApiError } from '@/client/service/utils';
import { currentAccountId } from '@/client/service/authn';

import {
  BulkEventsNotFoundError,
  CategoriesNotFoundError,
  MixedCalendarEventsError,
  InsufficientCalendarPermissionsError,
} from '@/common/exceptions/calendar';

const errorMap = {
  UrlNameAlreadyExistsError,
  InvalidUrlNameError,
  CalendarNotFoundError,
  CalendarEditorPermissionError,
  EditorAlreadyExistsError,
  EditorNotFoundError,
  BulkEventsNotFoundError,
  CategoriesNotFoundError,
  MixedCalendarEventsError,
  InsufficientCalendarPermissionsError,
  UnauthenticatedError,
  UnknownError,
  AccountInviteAlreadyExistsError,
};

/**
 * In-flight loads keyed by store instance, so concurrent callers for the same
 * account share one request. A load for another account is never shared.
 */
const inFlightLoads = new WeakMap<object, { accountId: string | null; promise: Promise<CalendarInfo[]> }>();

export default class CalendarService {
  store: ReturnType<typeof useCalendarStore>;

  constructor(store: ReturnType<typeof useCalendarStore> = useCalendarStore()) {
    this.store = store;
  }

  /**
   * Load every calendar the account owns or edits into the store, once.
   * Concurrent callers share the same request. Pass `forceLoad` after a
   * change the store cannot apply locally. A store loaded for a different
   * account (another account signed in through the session-expired modal,
   * with no logout between) is reset, list and selection, before refetching,
   * so a failed refetch leaves nothing of the previous account behind.
   * @returns Promise<Array<CalendarInfo>> The calendars with relationship info
   */
  async ensureCalendarsLoaded(forceLoad = false): Promise<CalendarInfo[]> {
    const accountId = currentAccountId();
    if (this.store.loaded && (this.store.accountId ?? null) !== accountId) {
      this.store.resetCalendars();
    }
    if (!forceLoad && this.store.loaded) {
      return this.store.calendarInfos;
    }
    const existing = inFlightLoads.get(this.store);
    if (existing && !forceLoad && existing.accountId === accountId) {
      return existing.promise;
    }
    const pending: Promise<CalendarInfo[]> = this.fetchCalendarInfos(accountId).finally(() => {
      // A forced or other-account load may have replaced this entry; only clear our own.
      if (inFlightLoads.get(this.store)?.promise === pending) {
        inFlightLoads.delete(this.store);
      }
    });
    inFlightLoads.set(this.store, { accountId, promise: pending });
    return pending;
  }

  /**
   * Fetch the account's calendars with relationship and replace the store's list.
   */
  private async fetchCalendarInfos(accountId: string | null): Promise<CalendarInfo[]> {
    try {
      const calendarsData = await ModelService.listModels('/api/v1/calendars');
      const infos = calendarsData.items.map((data) => CalendarInfo.fromObject(data));
      // A load that finishes after another account signed in must not overwrite the store.
      if (currentAccountId() === accountId) {
        this.store.setCalendarInfos(infos, accountId);
      }
      return infos;
    }
    catch (error) {
      console.error('Error loading calendars:', error);
      throw error;
    }
  }

  /**
   * Bare calendars, loading if needed. Kept for existing callers.
   * @returns Promise<Array<Calendar>> The list of calendars
   */
  async loadCalendars(forceLoad?: boolean): Promise<Array<Calendar>> {
    await this.ensureCalendarsLoaded(forceLoad ?? false);
    return this.store.calendars;
  }

  /**
   * Create a new calendar with the given name and optional title
   * @param urlName The URL name for the calendar
   * @param title Optional display title for the calendar
   * @returns Promise<Calendar> The newly created calendar
   */
  async createCalendar(urlName: string, title?: string): Promise<Calendar> {
    if (!urlName || urlName.trim() === '') {
      throw new EmptyValueError('urlName is empty');
    }

    try {
      const calendar = new Calendar(undefined, urlName.trim());

      // Set calendar title in content if provided
      if (title && title.trim()) {
        const content = calendar.content('en');
        content.name = title.trim();
      }

      const createdCalendar = await ModelService.createModel(
        calendar,
        '/api/v1/calendars',
      );
      const newCalendar = Calendar.fromObject(createdCalendar);

      // Add the new calendar to the store
      this.store.addCalendar(newCalendar);

      return newCalendar;
    }
    catch (error: unknown) {
      console.error('Error creating calendar:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Get a calendar by its URL name
   * @param urlName The URL name of the calendar
   * @returns Promise<Calendar|null> The calendar or null if not found
   */
  async getCalendarByUrlName(urlName: string): Promise<Calendar | null> {

    // Ensure calendars are loaded
    await this.loadCalendars();

    return this.store.getCalendarByUrlName(urlName);
  }
  /**
   * Get a calendar by its ID
   * @param calendarId The ID of the calendar
   * @returns Promise<Calendar|null> The calendar or null if not found
   */
  async getCalendarById(calendarId: string): Promise<Calendar | null> {
    // Ensure calendars are loaded
    await this.loadCalendars();

    return this.store.getCalendarById(calendarId);
  }

  /**
   * Updates a calendar on the server and in the store
   * @param calendar The calendar to update
   * @returns Promise<Calendar> The updated calendar
   */
  async updateCalendar(calendar: Calendar): Promise<Calendar> {
    try {
      const updatedCalendarData = await ModelService.updateModel(
        calendar,
        '/api/v1/calendars',
      );
      const updatedCalendar = Calendar.fromObject(updatedCalendarData);

      // Update the store
      this.store.updateCalendar(updatedCalendar);

      return updatedCalendar;
    }
    catch (error) {
      console.error('Error updating calendar:', error);
      throw error;
    }
  }

  /**
   * Deletes a calendar from the server and removes it from the store
   * @param calendar The calendar to delete
   * @returns Promise<void>
   */
  async deleteCalendar(calendar: Calendar): Promise<void> {
    try {
      await ModelService.deleteModel(calendar, '/api/v1/calendars');

      // Remove from the store
      this.store.removeCalendar(calendar);
    }
    catch (error) {
      console.error('Error deleting calendar:', error);
      throw error;
    }
  }

  /**
   * List all editors and pending invitations for a calendar
   * @param calendarId The ID of the calendar
   * @returns Promise<{activeEditors: CalendarEditor[], pendingInvitations: any[]}> Enhanced editors data
   */
  async listCalendarEditors(calendarId: string): Promise<{activeEditors: CalendarEditor[], pendingInvitations: any[]}> {
    try {
      const encodedId = validateAndEncodeId(calendarId, 'Calendar ID');
      const response = await axios.get(`/api/v1/calendars/${encodedId}/editors`);
      return {
        activeEditors: response.data.activeEditors.map((editor: Record<string, any>) => CalendarEditor.fromObject(editor)),
        pendingInvitations: response.data.pendingInvitations || [],
      };
    }
    catch (error: unknown) {
      console.error('Error listing calendar editors:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Grant edit access to a user for a calendar
   * @param calendarId The ID of the calendar
   * @param accountId The ID of the account to grant access
   * @returns Promise<CalendarEditor> The created editor relationship
   */
  async grantEditAccess(calendarId: string, email: string): Promise<CalendarEditor> {
    if (!email || email.trim() === '') {
      throw new EmptyValueError('accountId is empty');
    }

    try {
      const encodedId = validateAndEncodeId(calendarId, 'Calendar ID');
      const response = await axios.post(`/api/v1/calendars/${encodedId}/editors`, {
        email: email.trim(),
      });
      return CalendarEditor.fromObject(response.data);
    }
    catch (error: unknown) {
      console.error('Error granting edit access:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Revoke edit access from a user for a calendar
   * @param calendarId The ID of the calendar
   * @param editorId The ID of the editor record to revoke access from
   * @returns Promise<void>
   */
  async revokeEditAccess(calendarId: string, editorId: string): Promise<void> {
    // Validate editorId first with the expected error message
    if (!editorId || !editorId.trim()) {
      throw new EmptyValueError('editorId is empty');
    }

    try {
      const encodedCalendarId = validateAndEncodeId(calendarId, 'Calendar ID');
      const encodedEditorId = validateAndEncodeId(editorId, 'Editor ID');
      await axios.delete(`/api/v1/calendars/${encodedCalendarId}/editors/${encodedEditorId}`);
    }
    catch (error: unknown) {
      console.error('Error revoking edit access:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Leave a calendar the signed-in account edits: revoke the account's own
   * edit access, then drop the calendar from the store and, if it was the
   * current calendar, forget that selection. Needs only the account's own ID,
   * not the editor list, which the server shows to the owner alone. The
   * store is changed only after the server has agreed.
   * @param calendar The calendar to leave
   * @returns Promise<void>
   */
  async leaveCalendar(calendar: Calendar): Promise<void> {
    const accountId = currentAccountId();
    if (!accountId) {
      throw new EmptyValueError('no signed-in account');
    }
    await this.revokeEditAccess(calendar.id, accountId);
    this.store.removeCalendar(calendar);
    if (this.store.selectedCalendarId === calendar.id) {
      this.store.setSelectedCalendar(null);
    }
  }

  /**
   * Cancel a pending invitation for a calendar
   * @param calendarId The ID of the calendar
   * @param invitationId The ID of the invitation to cancel
   * @returns Promise<void>
   */
  async cancelInvitation(calendarId: string, invitationId: string): Promise<void> {
    try {
      const encodedCalendarId = validateAndEncodeId(calendarId, 'Calendar ID');
      const encodedInvitationId = validateAndEncodeId(invitationId, 'Invitation ID');
      await axios.delete(`/api/v1/calendars/${encodedCalendarId}/invitations/${encodedInvitationId}`);
    }
    catch (error: unknown) {
      console.error('Error canceling invitation:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Resend a pending invitation for a calendar
   * @param calendarId The ID of the calendar
   * @param invitationId The ID of the invitation to resend
   * @returns Promise<void>
   */
  async resendInvitation(calendarId: string, invitationId: string): Promise<void> {
    try {
      const encodedCalendarId = validateAndEncodeId(calendarId, 'Calendar ID');
      const encodedInvitationId = validateAndEncodeId(invitationId, 'Invitation ID');
      await axios.post(`/api/v1/calendars/${encodedCalendarId}/invitations/${encodedInvitationId}/resend`);
    }
    catch (error: unknown) {
      console.error('Error resending invitation:', error);
      handleApiError(error, errorMap);
    }
  }


  /**
   * Assign categories to multiple events in bulk
   * @param eventIds Array of event IDs to assign categories to
   * @param categoryIds Array of category IDs to assign
   * @returns Promise<CalendarEvent[]> Array of updated events with their categories
   */
  async bulkAssignCategories(eventIds: string[], categoryIds: string[]): Promise<CalendarEvent[]> {
    if (!Array.isArray(eventIds) || eventIds.length === 0) {
      throw new EmptyValueError('eventIds must be a non-empty array');
    }

    if (!Array.isArray(categoryIds) || categoryIds.length === 0) {
      throw new EmptyValueError('categoryIds must be a non-empty array');
    }

    try {
      const response = await axios.post('/api/v1/events/bulk-assign-categories', {
        eventIds,
        categoryIds,
      });

      return response.data.map((eventData: Record<string, any>) => CalendarEvent.fromObject(eventData));
    }
    catch (error: unknown) {
      console.error('Error in bulk category assignment:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Replace all category assignments for a single event
   * @param eventId The ID of the event to update
   * @param categoryIds Array of category IDs to assign (empty array clears all)
   * @param calendarId Optional calendar context for disambiguating repost edits
   *   when the user owns both the event's source calendar and a repost-target calendar.
   * @returns Promise<CalendarEvent> The updated event with its categories
   */
  async replaceEventCategories(
    eventId: string,
    categoryIds: string[],
    calendarId?: string,
  ): Promise<CalendarEvent> {
    try {
      const body: { categoryIds: string[]; calendarId?: string } = { categoryIds };
      if (calendarId) {
        body.calendarId = calendarId;
      }
      const response = await axios.put(`/api/v1/events/${eventId}/categories`, body);
      return CalendarEvent.fromObject(response.data);
    }
    catch (error: unknown) {
      console.error('Error replacing event categories:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Update calendar settings including content translations
   *
   * The three content fields are not symmetric on the server: `name` and
   * `description` are left alone when absent, while `imageAlt` is replaced —
   * an omitted value clears the stored column. A caller that sends content at
   * all must therefore send `imageAlt` for every language it sends, or it
   * erases alt text it never meant to touch.
   *
   * @param calendarId The ID of the calendar
   * @param settings The settings to update
   * @returns Promise<Calendar> The updated calendar
   */
  async updateCalendarSettings(
    calendarId: string,
    settings: {
      defaultDateRange?: DefaultDateRange;
      defaultEventImageId?: string | null;
      content?: Record<string, { name?: string; description?: string; imageAlt?: string }>;
    },
  ): Promise<Calendar> {
    try {
      const encodedId = validateAndEncodeId(calendarId, 'Calendar ID');
      const response = await axios.patch(`/api/v1/calendars/${encodedId}/settings`, settings);
      const updatedCalendar = Calendar.fromObject(response.data);

      // Update the store
      this.store.updateCalendar(updatedCalendar);

      return updatedCalendar;
    }
    catch (error: unknown) {
      console.error('Error updating calendar settings:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Fetch the widget display configuration for a calendar.
   * Returns defaults if no stored configuration exists.
   *
   * @param calendarId - The ID of the calendar
   * @returns The widget configuration
   */
  async getWidgetConfig(calendarId: string): Promise<WidgetConfig> {
    const encodedId = validateAndEncodeId(calendarId, 'Calendar ID');
    const response = await axios.get(`/api/v1/calendars/${encodedId}/widget/config`);
    return WidgetConfig.fromObject(response.data);
  }

  /**
   * Save the widget display configuration for a calendar.
   *
   * @param calendarId - The ID of the calendar
   * @param config - The widget configuration to save
   * @returns The saved widget configuration
   */
  async setWidgetConfig(calendarId: string, config: WidgetConfig): Promise<WidgetConfig> {
    const encodedId = validateAndEncodeId(calendarId, 'Calendar ID');
    const response = await axios.put(`/api/v1/calendars/${encodedId}/widget/config`, config.toObject());
    return WidgetConfig.fromObject(response.data);
  }
}
